import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Map from "../../components/common/Map";
import AggregationConfiguration from "../../components/common/AggregationConfiguration";
import { useMapData } from "../../hooks/useMapData";
import { useUrlState } from "../../hooks/useUrlState";
import { BoundingBox, MapGeometry } from "../../services/mapsApi";
import { runActivitiesJob } from "../../services/activitiesApi";
import type { ActivitiesJobParams, ActivityRow } from "../../services/activitiesApi";
import { runActivityDistributionsJob } from "../../services/activityDistributionsApi";
import type { ActivitiesRequest, DistributionJobParams, DistributionRow } from "../../services/activityDistributionsApi";
import { JobError } from "../../services/dataJobs";
import { getDemoRawRows, isDemoJobId } from "../../services/demoApi";
import { dashboardJobOf, hasActivitiesDashboard } from "../../services/activitiesDashboard";
import type { JobStatusResponse } from "../../services/dataJobs";
import { describeRequestError, isAbortError } from "../../services/http";
import { WebMercatorViewport } from "@deck.gl/core";
import NavBar from "../../components/common/NavBar";
import AvailableDatePicker from "../../components/data/AvailableDatePicker";
import AboutThisData, { deliveredDays } from "../../components/data/AboutThisData";
import DataStateNotice from "../../components/data/DataStateNotice";
import { CUT_PREVIEW_TEXT } from "./DistributionPreview";
import ResultCapNotice from "../../components/data/ResultCapNotice";
import JobStatusPanel from "../../components/data/JobStatusPanel";
import SourceBadge from "../../components/data/SourceBadge";
import ActivityModeControl, { TOTALS } from "../../components/data/ActivityModeControl";
import type { ActivityMode } from "../../components/data/ActivityModeControl";
import ExportAllRowsButton from "../../components/data/ExportAllRowsButton";
import DraftDataBadge from "../../components/data/DraftDataBadge";
import { useDataWindow } from "../../hooks/useDataWindow";
import { COUNTRIES, DEFAULT_COUNTRY, areaCountry, normalizeCountry } from "../../config/countries";
import type { CountryCode } from "../../config/countries";
import { ALL_ROLES_LABEL, DISTRIBUTIONS, DISTRIBUTION_ROLE_LABELS, GRANULARITY_LABELS, MEASURES, PRODUCT_LABELS, geoCapability, geoTypeLabel, offeredActivityMeasures, toApiGeoIds } from "../../config/products";
import type { ActivityMeasure, DistributionId, DistributionRole } from "../../config/products";
import { activityInitialScope } from "../../utils/navScope";
import { fitView } from "../../geo/areaShapes";
import { csvFilename } from "../../utils/csv";
import { formatDayLabel } from "../../utils/dateWindow";
import { submitBlockReason } from "../../utils/submitGuard";
import { pluralize } from "../../utils/plural";
import { activityBackState } from "../../utils/returnState";
import type { ActivityHandoff } from "../../utils/returnState";
import { pickerPath } from "../../utils/productPaths";
import { pickerDiffersFromJob, submittedFromScope } from "../../utils/submittedWindow";
import type { SubmittedWindow } from "../../utils/submittedWindow";

// The job preview returns at most this many rows, so a count equal to it is a cut-off, not a total.

const SAMPLE_DATA: Array<{
  position: [number, number];
  population: number;
  name: string;
}> = [];

/** A message under the Submit button: what went wrong, in a short title and a sentence. */
interface PageNotice {
  title: string;
  body: string;
}

/** The request a return trip hands back, if any. */
const backRequest = (state: unknown): ActivitiesRequest | null =>
  (state as { preserveState?: boolean; preservedRequest?: ActivitiesRequest } | null)?.preservedRequest ?? null;

/** A distribution and its role, as the page names them. */
const distributionLabel = (distribution: DistributionId, role: DistributionRole | null | undefined): string =>
  `${DISTRIBUTIONS[distribution].label} · ${role ? DISTRIBUTION_ROLE_LABELS[role] : ALL_ROLES_LABEL}`;

/** What a submitted request counted, for the switch; Totals with none. */
function modeOf(request: ActivitiesRequest | null): ActivityMode {
  return request?.kind === 'distributions'
    ? { kind: 'distributions', distribution: request.params.distribution, role: request.params.role ?? null }
    : TOTALS;
}

const Activities: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { filterState, addGridId, removeGridId } = useUrlState();
  // Where the page was opened from (saved area or a return trip from the preview). Read once, so
  // the very first render already knows the country and area type and asks for the right dates.
  const [navInit] = useState(() => activityInitialScope(location.state));
  const [selectedPoints, setSelectedPoints] = useState<typeof SAMPLE_DATA>([]);
  const [selectedGrids, setSelectedGrids] = useState<MapGeometry[]>([]);
  const [currentZoom, setCurrentZoom] = useState<number>(10);
  const [viewState, setViewState] = useState<any>(null);

  // API state management
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [jobResults, setJobResults] = useState<Array<ActivityRow | DistributionRow> | null>(null);
  // The API cut the preview of the job on screen (only distribution jobs ask).
  const [jobTruncated, setJobTruncated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  // The day and resolution the job on screen was submitted for. The picker stays live after a job
  // finishes, so results, the preview and the way back use this, never the picker's current value.
  const [submitted, setSubmitted] = useState<SubmittedWindow | null>(() => submittedFromScope(navInit));
  const [geoType, setGeoType] = useState<string | null>(navInit.geoType);
  const [countryCode, setCountryCode] = useState<CountryCode | null>(navInit.country);
  // The areas the job on screen was submitted for (the selection stays live, like the day).
  const [submittedIds, setSubmittedIds] = useState<string[] | null>(null);
  // What the job on screen was submitted as, for Export all rows and the preview.
  const [submittedRequest, setSubmittedRequest] = useState<ActivitiesRequest | null>(() => backRequest(location.state));
  // What the next job counts. Back from a preview, the choices of the job on screen.
  const [mode, setMode] = useState<ActivityMode>(() => modeOf(backRequest(location.state)));
  const [submitError, setSubmitError] = useState<PageNotice | null>(null);
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);

  // Which days and resolutions were actually delivered for this country, product and area type;
  // also the only source of the request's time window.
  const dw = useDataWindow({
    country: countryCode,
    product: 'activities',
    geoType,
    initial: { day: navInit.day, granularity: navInit.granularity },
  });
  const geoCap = geoCapability(countryCode, 'activities', geoType);
  // The measures the registry offers for this country and area type. The person's choice (or the
  // one restored on a return trip) is only used while it is still on offer; otherwise the first is.
  const measureOptions = useMemo(() => offeredActivityMeasures(countryCode, geoType), [countryCode, geoType]);
  const [chosenMeasure, setChosenMeasure] = useState<ActivityMeasure | null>(navInit.measure);
  const measure: ActivityMeasure | undefined =
    chosenMeasure && measureOptions.includes(chosenMeasure) ? chosenMeasure : measureOptions[0];
  // The measure the job on screen was submitted for (the picker stays live, like the day).
  const [submittedMeasure, setSubmittedMeasure] = useState<ActivityMeasure | null>(navInit.measure);
  // Distributions are served for the catalogue's area types only; anything else stays on Totals.
  const offersDistributions = hasActivitiesDashboard(geoType);
  const distributions = offersDistributions && mode.kind === 'distributions';
  const blockReason = submitBlockReason({ dw, selectionCount: selectedGrids.length });
  const pickerMoved = pickerDiffersFromJob(submitted, dw);
  // The Measure, Distribution or Role on screen is not what the job on screen was submitted for.
  const selectionMoved = submittedRequest !== null && (
    submittedRequest.kind === 'distributions'
      ? !distributions || mode.distribution !== submittedRequest.params.distribution || mode.role !== (submittedRequest.params.role ?? null)
      : distributions || measure !== submittedRequest.params.measure
  );
  const submittedLabel = submittedRequest?.kind === 'distributions'
    ? distributionLabel(submittedRequest.params.distribution, submittedRequest.params.role)
    : submittedMeasure ? MEASURES[submittedMeasure].label : null;

  const { geometries, loading, clearData, setGeometries } = useMapData();

  // The map opens on the selected areas; only while there are none (loading, or nothing framed)
  // does it show the selection's country. The country comes from the selection, never from the URL.
  const mapCountry: CountryCode = countryCode ?? DEFAULT_COUNTRY;
  const mapInitialView = useMemo(
    () => fitView(selectedGrids.map((g) => g.geometry), countryCode),
    [selectedGrids, countryCode],
  );

  // The job on screen. Leaving the page, or submitting again, stops waiting for it (the backend
  // job itself keeps running; only this client stops polling).
  const jobAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => jobAbortRef.current?.abort();
  }, []);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastApiCenterRef = useRef<{
    longitude: number;
    latitude: number;
  } | null>(null);
  const isInteractingRef = useRef<boolean>(false);

  const onPointClick = useCallback((info: any) => {
    if (info.object) {
      const point = info.object;
      setSelectedPoints((prev) => {
        const exists = prev.find((p) => p.name === point.name);
        if (exists) {
          return prev.filter((p) => p.name !== point.name);
        }
        return [...prev, point];
      });
    }
  }, []);

  const onGridClick = useCallback(
    (info: any) => {
      if (info.object && info.object.id) {
        const clickedGridId = info.object.id;

        // Look for the grid in both the main geometries and selectedGrids
        let gridGeometry = geometries.find((g) => g.geo_id === clickedGridId);
        if (!gridGeometry) {
          gridGeometry = selectedGrids.find((g) => g.geo_id === clickedGridId);
        }

        if (gridGeometry) {
          const exists = selectedGrids.find((g) => g.geo_id === clickedGridId);
          if (exists) {
            setSelectedGrids((prev) =>
              prev.filter((g) => g.geo_id !== clickedGridId)
            );
            removeGridId(clickedGridId);
            setSelectionNotice(null);
          } else if (selectedGrids.length < 10) {
            setSelectedGrids((prev) => [...prev, gridGeometry!]);
            addGridId(clickedGridId);
            setSelectionNotice(null);
          } else {
            setSelectionNotice("You can select at most 10 areas for one analysis. Remove one to add another.");
          }
        }
      }
    },
    [geometries, selectedGrids, addGridId, removeGridId]
  );

  const handleGridRemove = useCallback(
    (gridId: string) => {
      setSelectedGrids((prev) => prev.filter((g) => g.geo_id !== gridId));
      removeGridId(gridId);
      setSelectionNotice(null);
    },
    [removeGridId]
  );

  const handleSubmitJob = useCallback(async () => {
    // The Submit button is disabled with the reason underneath it; this is the same check again.
    if (blockReason || !dw.window || !dw.day || !geoCap || !countryCode || !geoType || !measure) return;

    // The ids go out in the type the area type's registry entry names (numbers for Estonian
    // municipalities); an id that cannot be converted is refused here, not sent as null.
    const ids = toApiGeoIds(geoCap.idType, selectedGrids.map((grid) => grid.geo_id));
    if (!ids.ok) {
      setSubmitError({ title: "Can't submit these areas", body: ids.reason });
      return;
    }

    // Stop waiting for any earlier job, then clear previous results and remember the window this job is run for
    jobAbortRef.current?.abort();
    const controller = new AbortController();
    jobAbortRef.current = controller;
    setSubmitError(null);
    setJobResults(null);
    setJobTruncated(false);
    setJobStatus(null);
    setCurrentJobId(null);
    // The instants that go into the request are the ones kept with the job (the CSV states them).
    const windowUtc = { start: dw.window.start_time, end: dw.window.end_time };
    setSubmitted({ day: dw.day, granularity: dw.granularity, windowUtc });
    setSubmittedMeasure(measure);
    setSubmittedIds(ids.ids.map(String));

    try {
      setIsSubmitting(true);

      const jobFields = {
        start_time: windowUtc.start,
        end_time: windowUtc.end,
        activity_type: dw.granularity,
        geo_type: geoType,
        geo_ids: ids.ids,
        country_code: countryCode,
        full: false, // the preview; Export all rows runs the full export
      };
      const jobOptions = {
        signal: controller.signal,
        onSubmitted: ({ jobId }: { jobId: string }) => {
          setCurrentJobId(jobId);
          setIsSubmitting(false);
          setIsPolling(true);
        },
        onStatusDetail: setJobStatus,
      };

      if (distributions) {
        const jobParams: DistributionJobParams = {
          agg_type: "activity_distributions",
          distribution: mode.distribution,
          ...(mode.role ? { role: mode.role } : {}),
          ...jobFields,
        };
        setSubmittedRequest({ kind: 'distributions', params: jobParams });
        const outcome = await runActivityDistributionsJob(jobParams, jobOptions);
        setJobTruncated(outcome.truncated === true);
        setJobResults(outcome.rows);
      } else {
        const jobParams: ActivitiesJobParams = { agg_type: "activities", measure, ...jobFields };
        setSubmittedRequest({ kind: 'totals', params: jobParams });
        const outcome = await runActivitiesJob(jobParams, jobOptions);
        setJobTruncated(outcome.truncated === true);
        setJobResults(outcome.rows);
      }

    } catch (error) {
      // Leaving the page or submitting again cancelled this job: nothing to show.
      if (isAbortError(error)) return;
      // A draft target says why it may fail, after the error itself.
      const body = [describeRequestError(error), geoCap?.pending?.reason].filter(Boolean).join(' ');
      setSubmitError(
        error instanceof JobError && error.kind === 'bad-response'
          ? { title: "Couldn't read the results", body }
          : { title: "The job didn't finish", body },
      );
    } finally {
      if (!controller.signal.aborted) {
        setIsSubmitting(false);
        setIsPolling(false);
      }
    }
  }, [blockReason, dw.window, dw.day, dw.granularity, geoCap, countryCode, geoType, measure, selectedGrids, distributions, mode]);

  // What the preview (and Back from it, or from the dashboard) needs of the job on screen.
  const jobHandoff = useCallback((): ActivityHandoff | null => {
    const previewMeasure = submittedMeasure ?? measure;
    if (!jobResults || !currentJobId || !previewMeasure) return null;

    return {
      jobId: currentJobId,
      jobResults: jobResults,
      jobStatus: jobStatus,
      // Pass geometry data for return navigation
      geometries: geometries,
      selectedGrids: selectedGrids,
      geoType: geoType,
      countryCode: countryCode,
      // The delivery day and resolution the job was submitted for (not the picker's current
      // value), so the preview is labelled truthfully and the way back keeps them
      day: submitted?.day,
      granularity: submitted?.granularity,
      // The measure the job was submitted for, so the preview names its figures after it
      measure: previewMeasure,
      windowUtc: submitted?.windowUtc ?? null,
      pickerSearch: navInit.pickerSearch,
      request: submittedRequest,
      truncated: jobTruncated,
    };
  }, [jobResults, currentJobId, jobStatus, geometries, selectedGrids, geoType, countryCode, submitted, submittedMeasure, measure, navInit.pickerSearch, submittedRequest, jobTruncated]);

  // This page's own history entry still holds the state it was opened with (the saved area, or
  // an earlier job). Give it the job, the areas, the day and the resolution first, in the very
  // form the preview's Back button hands back, so the browser's Back button lands on the same
  // screen instead of an empty one.
  const keepJobForBack = useCallback((handoff: ActivityHandoff) => {
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: activityBackState(handoff) }
    );
  }, [navigate, location.pathname, location.search, location.hash]);

  const handlePreview = useCallback(() => {
    const handoff = jobHandoff();
    if (!handoff) return;
    keepJobForBack(handoff);
    navigate('/activity-preview', { state: handoff });
  }, [jobHandoff, keepJobForBack, navigate]);

  // An area made on the catalogue 1.0 maps: the dashboard runs this job again in full, with its
  // distributions, from the data API. The job's own areas, day and measure, never the picker's.
  const dashboardJob = useMemo(
    () =>
      dashboardJobOf({
        ids: submittedIds ?? [],
        geoType,
        countryCode,
        day: submitted?.day ?? null,
        granularity: submitted?.granularity ?? null,
        windowUtc: submitted?.windowUtc ?? null,
        measure: submittedMeasure ?? measure ?? null,
      }),
    [submittedIds, geoType, countryCode, submitted, submittedMeasure, measure],
  );

  const exportName = csvFilename(
    'activities-all-rows',
    [
      countryCode ? COUNTRIES[countryCode].label : '',
      submitted?.day,
      submitted?.granularity,
      ...(submittedRequest?.kind === 'distributions' ? [submittedRequest.params.distribution, submittedRequest.params.role] : []),
    ].filter(Boolean).join(' '),
  );

  const handleViewCatalogueDashboard = useCallback(() => {
    const handoff = jobHandoff();
    if (!handoff || !dashboardJob) return;
    keepJobForBack(handoff);
    navigate('/activity-dashboard', {
      state: {
        catalogueJob: dashboardJob,
        selectedGrids,
        countryCode,
        measure: dashboardJob.params.measure,
      },
    });
  }, [jobHandoff, keepJobForBack, navigate, dashboardJob, selectedGrids, countryCode]);

  // Demo build only: the dashboard wants the columns the demo API sent, not the preview rows.
  const handleViewDashboard = useCallback(() => {
    if (!jobResults || !currentJobId) return;
    navigate('/activity-dashboard', {
      state: {
        jobId: currentJobId,
        jobResults: getDemoRawRows(currentJobId),
        jobStatus,
        geometries,
        selectedGrids,
        geoType,
        countryCode,
        measure: 'unique_people',
      },
    });
  }, [jobResults, currentJobId, jobStatus, navigate, geometries, selectedGrids, geoType, countryCode]);

  const hasCenterChangedSignificantly = useCallback((newViewState: any) => {
    if (!lastApiCenterRef.current) {
      return true;
    }

    const { longitude: oldLng, latitude: oldLat } = lastApiCenterRef.current;
    const { longitude: newLng, latitude: newLat } = newViewState;

    const threshold = 0.01 / Math.pow(2, newViewState.zoom - 10);

    const deltaLng = Math.abs(newLng - oldLng);
    const deltaLat = Math.abs(newLat - oldLat);

    return deltaLng > threshold || deltaLat > threshold;
  }, []);

  const onViewStateChange = useCallback((newViewState: any) => {
    setCurrentZoom(newViewState.zoom);
    setViewState(newViewState);

    isInteractingRef.current = true;

    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    interactionTimeoutRef.current = setTimeout(() => {
      isInteractingRef.current = false;
    }, 300);
  }, []);

  const getBoundingBox = useCallback(() => {
    if (!viewState || !viewState.width || !viewState.height) {
      return null;
    }

    const viewport = new WebMercatorViewport(viewState);

    const [west, south] = viewport.unproject([0, viewState.height]);
    const [east, north] = viewport.unproject([viewState.width, 0]);

    return {
      lng1: west,
      lat1: south,
      lng2: east,
      lat2: north,
    };
  }, [viewState]);

  useEffect(() => {
    const state = location.state as {
      savedArea?: any;
      geometries?: MapGeometry[];
      wktGeometry?: string;
      preserveState?: boolean;
      jobId?: string;
      jobResults?: Array<ActivityRow | DistributionRow>;
      preservedTruncated?: boolean;
      jobStatus?: JobStatusResponse;
      preservedGeometries?: any[];
      preservedSelectedGrids?: any[];
      preservedGeoType?: string;
      preservedCountryCode?: string;
      preservedDay?: string;
      preservedGranularity?: 'hourly' | 'daily';
    } | null;

    if (state?.preserveState) {
      // Handle returning from preview - restore job state
      if (state.jobId) {
        setCurrentJobId(state.jobId);
      }
      if (state.jobResults) {
        setJobResults(state.jobResults);
        setJobTruncated(state.preservedTruncated === true);
      }
      if (state.jobStatus) {
        setJobStatus(state.jobStatus);
      }

      // Restore geometry data
      if (state.preservedGeometries) {
        setGeometries(state.preservedGeometries);
        console.log('Activities: Restored geometries from preview:', state.preservedGeometries.length);
      }
      if (state.preservedSelectedGrids) {
        setSelectedGrids(state.preservedSelectedGrids);
        // The selection handed back is the one the job on screen was run for.
        if (state.jobId) setSubmittedIds(state.preservedSelectedGrids.map((g: any) => String(g.geo_id)));
        // Back from the preview: frame the selection again
        if (state.preservedSelectedGrids.length > 0) {
          const framed = fitView(state.preservedSelectedGrids.map((g: any) => g.geometry), normalizeCountry(state.preservedCountryCode));
          setViewState({ ...framed, pitch: 0, bearing: 0 });
        }
        console.log('Activities: Restored selected grids from preview:', state.preservedSelectedGrids.length);

        // Add grid IDs back to URL state
        state.preservedSelectedGrids.forEach((grid: any) => {
          addGridId(grid.geo_id);
        });
      }
      if (state.preservedGeoType) {
        setGeoType(state.preservedGeoType);
        console.log('Activities: Restored geo type from preview:', state.preservedGeoType);
      }
      const cc = normalizeCountry(state.preservedCountryCode);
      if (cc) setCountryCode(cc);

      console.log('Activities: Restored job state and geometries from preview');
    } else if (state?.savedArea && state?.geometries) {
      // Handle standard polygons with IDs
      // The geometries from the area shapes loader are already GeoJSON - keep geometry as-is
      const processedGeometries = state.geometries.map((g: any) => ({
        geo_id: String(g.geo_id || g.grid_id),
        geometry: g.geometry,
      }));

      // Extract and set the geo type from the saved area
      const savedAreaGeoType = state.savedArea.geo_type || state.savedArea.type;
      if (savedAreaGeoType) {
        setGeoType(savedAreaGeoType);
        console.log('Activities: Set geo type from saved area:', savedAreaGeoType);
      }

      // The saved area's country (a legacy area saved without one is a Norwegian one)
      setCountryCode(areaCountry(state.savedArea));

      // Set the geometries in the map data hook
      setGeometries(processedGeometries);
      console.log('Activities: Set geometries from saved area:', processedGeometries.length, 'geometries');

      // Find grids that match the saved area IDs (now using processed geometries)
      const savedAreaGrids = processedGeometries.filter(
        (g) =>
          state.savedArea.ids.includes(Number(g.geo_id)) ||
          state.savedArea.ids.includes(g.geo_id)
      );

      console.log('Activities: Found matching grids:', savedAreaGrids.length, 'out of', state.savedArea.ids.length, 'expected');

      // Update selected grids state
      setSelectedGrids(savedAreaGrids);

      // Add grid IDs to URL state
      savedAreaGrids.forEach((grid) => {
        addGridId(grid.geo_id);
      });

      // Frame the map on the selected geometries
      if (savedAreaGrids.length > 0) {
        setTimeout(() => {
          const framed = fitView(savedAreaGrids.map((g) => g.geometry), areaCountry(state.savedArea));
          setViewState((prev: any) => ({
            ...prev,
            ...framed,
            pitch: 0,
            bearing: 0,
            transitionDuration: 1000,
          }));
        }, 100);
      }
    } else if (state?.savedArea && state?.wktGeometry) {
      // Handle WKT polygon - create a custom geometry object
      // Extract and set the geo type from the saved area
      const savedAreaGeoType = state.savedArea.geo_type || state.savedArea.type;
      if (savedAreaGeoType) {
        setGeoType(savedAreaGeoType);
        console.log('Activities: Set geo type from WKT saved area:', savedAreaGeoType);
      }

      // The saved area's country (a legacy area saved without one is a Norwegian one)
      setCountryCode(areaCountry(state.savedArea));

      // Create a custom geometry entry for the WKT polygon
      const wktGeometry: MapGeometry = {
        geo_id: `wkt_${state.savedArea.id}`,
        geometry: state.wktGeometry
      };

      // Set the WKT geometry as the only geometry
      setGeometries([wktGeometry]);

      // Set it as selected
      setSelectedGrids([wktGeometry]);
      addGridId(wktGeometry.geo_id);

      // Frame the map on the WKT geometry
      setTimeout(() => {
        const framed = fitView([wktGeometry.geometry], areaCountry(state.savedArea));
        setViewState((prev: any) => ({
          ...prev,
          ...framed,
          pitch: 0,
          bearing: 0,
          transitionDuration: 1000,
        }));
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const debouncedFetchMapData = useCallback(
    (
      boundingBox: BoundingBox,
      center: { longitude: number; latitude: number }
    ) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (!isInteractingRef.current) {
          lastApiCenterRef.current = center;
        }
      }, 500);
    },
    []
  );

  useEffect(() => {
    if (currentZoom > 13 && viewState) {
      const boundingBox = getBoundingBox();

      if (boundingBox) {
        const center = {
          longitude: viewState.longitude,
          latitude: viewState.latitude,
        };

        if (hasCenterChangedSignificantly(viewState)) {
          debouncedFetchMapData(boundingBox as BoundingBox, center);
        }
      }
    } else if (currentZoom <= 13) {
      clearData();
      lastApiCenterRef.current = null;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    }
  }, [
    currentZoom,
    viewState,
    getBoundingBox,
    hasCenterChangedSignificantly,
    debouncedFetchMapData,
    clearData,
  ]);

  useEffect(() => {
    // Only clear selected grids if there are no grid IDs and we're not loading a saved area or preserving state
    const state = location.state as { preserveState?: boolean; savedArea?: any } | null;
    if (filterState.gridIds.length === 0 && !state?.preserveState && !state?.savedArea) {
      setSelectedGrids([]);
    }
  }, [filterState.gridIds, location.state]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="h-screen flex">
      <div className="flex-1 flex flex-col">
        <NavBar title="Activities" showBackButton backTo={pickerPath('activities', navInit.pickerSearch)} />

        {/* Collapsible Sections */}
        <div className="flex-1 overflow-y-auto">

          {/* Map Section */}
          <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
            <input type="checkbox" defaultChecked />
            <div className="collapse-title text-lg font-medium flex items-center space-x-2">
              <span>🗺️ Interactive Map</span>
              <div className="badge badge-sm badge-outline">{pluralize(selectedGrids.length, 'area')} selected</div>
            </div>
            <div className="collapse-content p-0">
              <div className="h-96">
                <Map
                  key={mapCountry}
                  initialView={mapInitialView}
                  viewState={viewState ?? undefined}
                  data={SAMPLE_DATA}
                  selectedPoints={selectedPoints}
                  onPointClick={onPointClick}
                  onViewStateChange={onViewStateChange}
                  geometries={geometries}
                  loading={loading}
                  selectedGrids={selectedGrids}
                  onGridClick={onGridClick}
                />
              </div>
              {selectionNotice && (
                <div className="p-4">
                  <DataStateNotice tone="warn" title="Selection limit reached" body={selectionNotice} />
                </div>
              )}
            </div>
          </div>

          {/* Job Submission Section */}
          <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
            <input type="checkbox" defaultChecked />
            <div className="collapse-title text-lg font-medium flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span>🚀 Submit Analysis Job</span>
              </div>
            </div>
            <div className="collapse-content">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                <div>
                  <span className="text-ink-muted">Product:</span>
                  <span className="ml-2 font-medium">{PRODUCT_LABELS.activities}</span>
                  {countryCode ? (
                    <span className="ml-2 align-middle">
                      <SourceBadge country={countryCode} product="activities" />
                      <DraftDataBadge country={countryCode} product="activities" geoType={geoType} />
                    </span>
                  ) : null}
                </div>
                <div>
                  <span className="text-ink-muted">{distributions ? 'Distribution:' : 'Measure:'}</span>
                  <span className="ml-2 font-medium">
                    {distributions
                      ? distributionLabel(mode.distribution, mode.role)
                      : measure ? MEASURES[measure].label : '–'}
                  </span>
                </div>
                <div>
                  <span className="text-ink-muted">Time resolution:</span>
                  <span className="ml-2 font-medium">{GRANULARITY_LABELS[dw.granularity]}</span>
                </div>
                <div>
                  <span className="text-ink-muted">Area type:</span>
                  <span className="ml-2 font-medium">{geoType ? geoTypeLabel(geoType, countryCode) : '–'}</span>
                </div>
              </div>

              {countryCode ? (
                <div className="mb-4">
                  <AboutThisData country={countryCode} product="activities" measure={distributions ? undefined : measure} distributions={distributions} dates={deliveredDays(dw)} />
                </div>
              ) : null}

              {submitError && (
                <div className="mb-4">
                  <DataStateNotice tone="error" title={submitError.title} body={submitError.body} />
                </div>
              )}

              <div className="flex justify-between items-center">
                <div className="text-sm text-ink-muted">
                  Selected: {pluralize(selectedGrids.length, 'area')} · {dw.day ? formatDayLabel(dw.day) : 'No date'} · {GRANULARITY_LABELS[dw.granularity]} · UTC
                </div>
                <button
                  className={`btn btn-primary btn-sm ${(isSubmitting || isPolling || !!blockReason) ? 'btn-disabled' : ''}`}
                  onClick={handleSubmitJob}
                  disabled={isSubmitting || isPolling || !!blockReason}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      Submitting...
                    </>
                  ) : isPolling ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      Processing...
                    </>
                  ) : (
                    'Submit Job'
                  )}
                </button>
              </div>
              {blockReason && <p className="text-xs text-ink-muted mt-2">{blockReason}</p>}
            </div>
          </div>

          {/* Job Status Section */}
          {currentJobId && !jobResults && (
            <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
              <input type="checkbox" defaultChecked />
              <div className="collapse-title text-lg font-medium">📊 Job Status</div>
              <div className="collapse-content">
                <JobStatusPanel jobId={currentJobId} status={jobStatus} />
              </div>
            </div>
          )}

          {/* Job Results Section */}
          {jobResults && currentJobId && (
            <div className="collapse collapse-arrow bg-surface border-b border-paper-200">
              <input type="checkbox" defaultChecked />
              <div className="collapse-title text-lg font-medium flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span>📈 Job Results</span>
                  <div className="badge badge-success badge-sm">
                    {jobTruncated
                      ? `First ${jobResults.length} rows (preview limit)`
                      : pluralize(jobResults.length, 'result')}
                  </div>
                </div>
                <div className="text-xs text-ink-muted font-mono">
                  Job ID: {currentJobId.substring(0, 8)}...
                </div>
              </div>
              <div className="collapse-content">
                {submitted && (
                  <p className="text-sm text-ink-muted mb-3">
                    Results for {submittedLabel ? `${submittedLabel} · ` : ''}{formatDayLabel(submitted.day)} · {GRANULARITY_LABELS[submitted.granularity]} · UTC
                  </p>
                )}
                {(pickerMoved || selectionMoved) && (
                  <div className="mb-3">
                    <DataStateNotice
                      tone="info"
                      title={selectionMoved ? 'What is selected has changed' : 'The day or time resolution has changed'}
                      body={selectionMoved
                        ? 'These results are still the ones submitted earlier. Submit again to get what is selected now.'
                        : 'These results are still the ones submitted earlier. Submit again to get the day shown in the data window.'}
                    />
                  </div>
                )}
                {submittedRequest?.kind === 'distributions' ? (
                  jobTruncated ? (
                    <div className="mb-3">
                      <DataStateNotice tone="warn" title="Only part of the rows" body={CUT_PREVIEW_TEXT} />
                    </div>
                  ) : null
                ) : (
                  <div className="mb-3">
                    <ResultCapNotice rows={jobResults} truncated={jobTruncated} />
                  </div>
                )}
                {/* Action Buttons - moved inside content */}
                <div className="flex justify-end space-x-2 mb-4 pb-3 border-b border-base-300">
                  <button
                    className={`btn btn-outline btn-sm ${!jobResults ? 'btn-disabled' : ''}`}
                    disabled={!jobResults}
                    onClick={handlePreview}
                    title={!jobResults ? 'Complete job to enable preview' : jobTruncated ? `View results preview (first ${jobResults.length} rows)` : 'View results preview'}
                  >
                    📋 Preview
                  </button>
                  {isDemoJobId(currentJobId) ? (
                    <button
                      className={`btn btn-outline btn-sm ${!jobResults ? 'btn-disabled' : ''}`}
                      disabled={!jobResults}
                      onClick={handleViewDashboard}
                      title={!jobResults ? 'Complete job to enable dashboard' : 'View results dashboard'}
                    >
                      📊 View Dashboard
                    </button>
                  ) : dashboardJob ? (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={handleViewCatalogueDashboard}
                      title="View the complete results with their distributions"
                    >
                      📊 View Dashboard
                    </button>
                  ) : null}
                </div>
                {submittedRequest && !isDemoJobId(currentJobId) ? (
                  <div className="flex justify-end mb-4">
                    <ExportAllRowsButton request={submittedRequest} filename={exportName} />
                  </div>
                ) : null}
                {jobStatus && (
                  <div className="mb-4">
                    <JobStatusPanel jobId={currentJobId} status={jobStatus} />
                  </div>
                )}

                {/* The raw answer, for people who work against the API; closed by default */}
                <details className="bg-paper-100 text-ink rounded-lg p-3 mb-4">
                  <summary className="cursor-pointer text-xs font-medium">Show the raw API response (first 2 rows)</summary>
                  <pre className="text-xs text-ink-muted overflow-x-auto max-h-32 mt-2">
{JSON.stringify({
  job_id: currentJobId,
  status: "completed",
  preview: jobResults.slice(0, 2)
}, null, 2)}
                  </pre>
                </details>
              </div>
            </div>
          )}

        </div>


      </div>

      <AggregationConfiguration
        selectedGrids={selectedGrids}
        onGridRemove={handleGridRemove}
        modeControl={offersDistributions ? <ActivityModeControl mode={mode} onChange={setMode} /> : undefined}
        measureLabel={measure && !distributions ? MEASURES[measure].label : undefined}
        measureOptions={distributions ? undefined : measureOptions.map((m) => ({ value: m, label: MEASURES[m].label }))}
        measure={distributions ? undefined : measure}
        onMeasureChange={distributions ? undefined : setChosenMeasure}
        dataControl={<AvailableDatePicker dw={dw} />}
      />
    </div>
  );
};

export default Activities;